/** Single folder in the storage tree — expand/collapse, select, and an actions menu (rename, new subfolder, delete) */

import { useState, useRef, useEffect } from 'react'
import { toast } from 'sonner'
import { ChevronRight, Folder, FolderOpen, MoreHorizontal, Pencil, Trash2, FolderPlus } from 'lucide-react'
import { cn } from '../../lib/utils'
import { useT } from '../../i18n'
import { useUpdateStorageFolder, useDeleteStorageFolder, useCreateStorageFolder } from '../../hooks/use-storage'
import { ConfirmDeleteDialog } from './storage-dialogs'
import type { StorageFolderNode as StorageFolder } from '../../hooks/use-storage'

interface StorageFolderNodeProps {
  folder: StorageFolder
  depth: number
  activeFolderId: string | null
  /** Ids from the root to the open folder; those nodes expand automatically */
  activePath: ReadonlySet<string>
  onSelect: (id: string | null) => void
}

const inlineInputClass = 'min-w-0 flex-1 rounded-md border border-accent bg-surface px-1.5 py-0.5 text-sm text-fg outline-none placeholder:text-fg-subtle'

export function StorageFolderNode({ folder, depth, activeFolderId, activePath, onSelect }: StorageFolderNodeProps) {
  const t = useT()
  const onPath = activePath.has(folder.id) && folder.id !== activeFolderId
  const [expanded, setExpanded] = useState(onPath)
  const [menuOpen, setMenuOpen] = useState(false)
  const [renaming, setRenaming] = useState(false)
  const [renameValue, setRenameValue] = useState(folder.name)
  const [addingChild, setAddingChild] = useState(false)
  const [newChildName, setNewChildName] = useState('')
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)
  const menuButtonRef = useRef<HTMLButtonElement>(null)
  const renameRef = useRef<HTMLInputElement>(null)
  const isActive = activeFolderId === folder.id
  const hasChildren = folder.children.length > 0

  const updateFolder = useUpdateStorageFolder()
  const deleteFolder = useDeleteStorageFolder()
  const createFolder = useCreateStorageFolder()

  const fail = (err: unknown) => toast.error(t('storage.error.generic', { message: err instanceof Error ? err.message : String(err) }))

  // Expand when navigation lands below this folder (e.g. via a subfolder card)
  useEffect(() => {
    if (onPath) setExpanded(true)
  }, [onPath])

  // Close menu on outside click or Escape (focus back on the menu button)
  useEffect(() => {
    if (!menuOpen) return
    const onMouseDown = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false)
    }
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setMenuOpen(false)
        menuButtonRef.current?.focus()
      }
    }
    document.addEventListener('mousedown', onMouseDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('mousedown', onMouseDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [menuOpen])

  useEffect(() => {
    if (renaming) renameRef.current?.select()
  }, [renaming])

  const handleRenameSubmit = async () => {
    const name = renameValue.trim()
    setRenaming(false)
    if (!name || name === folder.name) {
      setRenameValue(folder.name)
      return
    }
    try {
      await updateFolder.mutateAsync({ id: folder.id, name })
    } catch (err) {
      setRenameValue(folder.name)
      fail(err)
    }
  }

  const confirmDelete = async () => {
    try {
      await deleteFolder.mutateAsync(folder.id)
      setDeleteConfirmOpen(false)
      toast.success(t('storage.folder.deleted', { name: folder.name }))
      if (isActive || activePath.has(folder.id)) onSelect(folder.parentId)
    } catch (err) {
      fail(err)
    }
  }

  const handleChildSubmit = async () => {
    const name = newChildName.trim()
    setAddingChild(false)
    setNewChildName('')
    if (!name) return
    try {
      await createFolder.mutateAsync({ name, parentId: folder.id })
    } catch (err) {
      fail(err)
    }
  }

  const menuItems = [
    { icon: Pencil, label: t('storage.folder.rename'), action: () => setRenaming(true) },
    {
      icon: FolderPlus,
      label: t('storage.folder.newSubfolder'),
      action: () => {
        setExpanded(true)
        setAddingChild(true)
      },
    },
    { icon: Trash2, label: t('storage.folder.delete'), action: () => setDeleteConfirmOpen(true), danger: true },
  ]

  return (
    <li>
      <div
        className={cn(
          'group flex items-center gap-1 rounded-md py-0.5 pr-1 text-sm',
          isActive ? 'bg-accent-soft text-accent-text' : 'text-fg-muted hover:bg-surface-hover hover:text-fg',
        )}
        style={{ paddingLeft: `${4 + depth * 14}px` }}
      >
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className={cn('shrink-0 cursor-pointer rounded p-0.5 hover:bg-surface-hover', !hasChildren && 'invisible')}
          aria-label={expanded ? t('storage.folder.collapseNamed', { name: folder.name }) : t('storage.folder.expandNamed', { name: folder.name })}
          aria-expanded={hasChildren ? expanded : undefined}
          tabIndex={hasChildren ? 0 : -1}
        >
          <ChevronRight className={cn('h-3.5 w-3.5 transition-transform', expanded && 'rotate-90')} aria-hidden="true" />
        </button>

        {renaming ? (
          <input
            ref={renameRef}
            value={renameValue}
            onChange={(e) => setRenameValue(e.target.value)}
            onBlur={() => void handleRenameSubmit()}
            onKeyDown={(e) => {
              // Enter submits through onBlur so the rename runs once
              if (e.key === 'Enter') e.currentTarget.blur()
              if (e.key === 'Escape') {
                setRenameValue(folder.name)
                setRenaming(false)
              }
            }}
            maxLength={200}
            aria-label={t('storage.folder.name')}
            className={inlineInputClass}
          />
        ) : (
          <button
            type="button"
            onClick={() => {
              onSelect(folder.id)
              if (hasChildren) setExpanded(true)
            }}
            aria-current={isActive ? 'page' : undefined}
            className={cn('flex min-w-0 flex-1 cursor-pointer items-center gap-1.5 py-1 text-left', isActive && 'font-medium')}
          >
            {expanded && hasChildren ? <FolderOpen className="h-4 w-4 shrink-0" aria-hidden="true" /> : <Folder className="h-4 w-4 shrink-0" aria-hidden="true" />}
            <span className="truncate">{folder.name}</span>
          </button>
        )}

        <div className="relative" ref={menuRef}>
          <button
            ref={menuButtonRef}
            type="button"
            onClick={() => setMenuOpen((v) => !v)}
            aria-label={t('storage.folder.actionsNamed', { name: folder.name })}
            aria-haspopup="menu"
            aria-expanded={menuOpen}
            className={cn(
              'shrink-0 cursor-pointer rounded p-0.5 text-fg-subtle hover:bg-surface-hover hover:text-fg focus:opacity-100 group-hover:opacity-100',
              menuOpen ? 'opacity-100' : 'opacity-0',
            )}
          >
            <MoreHorizontal className="h-4 w-4" aria-hidden="true" />
          </button>

          {menuOpen && (
            <div role="menu" className="absolute right-0 top-full z-30 mt-1 min-w-[10rem] rounded-md border border-border bg-surface-raised py-1 shadow-lg">
              {menuItems.map(({ icon: Icon, label, action, danger }) => (
                <button
                  key={label}
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    setMenuOpen(false)
                    action()
                  }}
                  className={cn(
                    'flex w-full cursor-pointer items-center gap-2 px-3 py-1.5 text-left text-sm',
                    danger ? 'text-danger hover:bg-danger-soft' : 'text-fg-muted hover:bg-surface-hover hover:text-fg',
                  )}
                >
                  <Icon className="h-3.5 w-3.5" aria-hidden="true" />
                  {label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {(expanded && hasChildren) || addingChild ? (
        <ul>
          {expanded &&
            folder.children.map((child) => (
              <StorageFolderNode
                key={child.id}
                folder={child}
                depth={depth + 1}
                activeFolderId={activeFolderId}
                activePath={activePath}
                onSelect={onSelect}
              />
            ))}
          {addingChild && (
            <li className="flex items-center gap-1.5 py-1 pr-1" style={{ paddingLeft: `${4 + (depth + 1) * 14 + 20}px` }}>
              <Folder className="h-4 w-4 shrink-0 text-fg-subtle" aria-hidden="true" />
              <input
                autoFocus
                value={newChildName}
                onChange={(e) => setNewChildName(e.target.value)}
                onBlur={() => void handleChildSubmit()}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') e.currentTarget.blur()
                  if (e.key === 'Escape') {
                    setNewChildName('')
                    setAddingChild(false)
                  }
                }}
                maxLength={200}
                placeholder={t('storage.folder.name')}
                aria-label={t('storage.folder.subfolderNameIn', { name: folder.name })}
                className={inlineInputClass}
              />
            </li>
          )}
        </ul>
      ) : null}

      {deleteConfirmOpen && (
        <ConfirmDeleteDialog
          title={t('storage.folder.deleteTitle')}
          description={t('storage.folder.deleteConfirm', { name: folder.name })}
          confirmLabel={t('storage.folder.delete')}
          pending={deleteFolder.isPending}
          onClose={() => setDeleteConfirmOpen(false)}
          onConfirm={() => void confirmDelete()}
        />
      )}
    </li>
  )
}
